// Component: Q&A Pair List - Table with filters, pagination, actions

import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  message,
  Tooltip,
  Popconfirm
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  ThunderboltOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { qaPairService } from '../../../services/qaPairService';
import type { QAPair, QAPairListParams } from '../../../types/qaPair';

const { Search } = Input;
const { Option } = Select;

const QAPairList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [qaPairs, setQaPairs] = useState<QAPair[]>([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<QAPairListParams>({
    page: 1,
    limit: 20,
    status: undefined,
    reviewStatus: undefined,
    category: undefined,
    search: undefined
  });

  // Load data
  const loadData = async () => {
    setLoading(true);
    try {
      const response = await qaPairService.list(filters);
      setQaPairs(response.data.items);
      setTotal(response.data.pagination.total);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Không thể tải danh sách Q&A pairs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filters]);

  // Handle pagination
  const handleTableChange = (pagination: TablePaginationConfig) => {
    setFilters({
      ...filters,
      page: pagination.current || 1,
      limit: pagination.pageSize || 20
    });
  };

  // Handle search
  const handleSearch = (value: string) => {
    setFilters({
      ...filters,
      search: value || undefined,
      page: 1
    });
  };

  // Handle filter change
  const handleFilterChange = (key: keyof QAPairListParams, value: any) => {
    setFilters({
      ...filters,
      [key]: value || undefined,
      page: 1
    });
  };

  // Handle approve
  const handleApprove = async (id: number) => {
    try {
      await qaPairService.approve(id);
      message.success('Đã duyệt Q&A pair thành công');
      loadData();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Không thể duyệt Q&A pair');
    }
  };

  // Handle reject
  const handleReject = async (id: number) => {
    try {
      await qaPairService.reject(id);
      message.success('Đã từ chối Q&A pair');
      loadData();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Không thể từ chối Q&A pair');
    }
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    try {
      await qaPairService.delete(id);
      message.success('Đã xóa Q&A pair');
      loadData();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Không thể xóa Q&A pair');
    }
  };

  // Handle generate embedding
  const handleGenerateEmbedding = async (pairId: number) => {
    const hide = message.loading('Đang tạo embedding với Google AI...', 0);
    try {
      const response = await qaPairService.generateEmbedding(pairId);
      hide();
      
      // Show detailed success message
      message.success({
        content: (
          <div>
            <strong>✅ Embedding đã được tạo thành công!</strong>
            <br />
            <small style={{ color: '#52c41a' }}>
              Pair ID {pairId} • Model: text-embedding-004 • Dimensions: 768
            </small>
          </div>
        ),
        duration: 5
      });
      
      loadData();
    } catch (error: any) {
      hide();
      const errorMsg = error.response?.data?.message || 'Không thể tạo embedding';
      
      message.error({
        content: (
          <div>
            <strong>❌ Lỗi khi tạo embedding</strong>
            <br />
            <small style={{ color: '#ff4d4f' }}>{errorMsg}</small>
            <br />
            <small style={{ color: '#999' }}>
              💡 Kiểm tra: Google AI API key, quota limit, hoặc xem backend logs
            </small>
          </div>
        ),
        duration: 10
      });
    }
  };

  // Columns definition
  const columns: ColumnsType<QAPair> = [
    {
      title: 'ID',
      dataIndex: 'PairID',
      key: 'PairID',
      width: 70,
      fixed: 'left'
    },
    {
      title: 'Câu hỏi',
      dataIndex: 'Question',
      key: 'Question',
      width: 300,
      ellipsis: {
        showTitle: false
      },
      render: (text: string) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      )
    },
    {
      title: 'Câu trả lời',
      dataIndex: 'Answer',
      key: 'Answer',
      width: 300,
      ellipsis: {
        showTitle: false
      },
      render: (text: string) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      )
    },
    {
      title: 'Danh mục',
      dataIndex: 'Category',
      key: 'Category',
      width: 150,
      render: (category: string) => (
        <Tag color="blue">{category || 'Chưa phân loại'}</Tag>
      )
    },
    {
      title: 'Tags',
      dataIndex: 'Tags',
      key: 'Tags',
      width: 200,
      ellipsis: {
        showTitle: false
      },
      render: (tags: string) => {
        if (!tags) return <span style={{ color: '#999' }}>Chưa có</span>;
        
        const tagList = tags.split(',').map(t => t.trim()).filter(Boolean);
        return (
          <Space size={[0, 4]} wrap>
            {tagList.slice(0, 3).map((tag, index) => (
              <Tag key={index} color="cyan" style={{ marginBottom: 4 }}>
                {tag}
              </Tag>
            ))}
            {tagList.length > 3 && (
              <Tooltip title={tagList.slice(3).join(', ')}>
                <Tag color="default">+{tagList.length - 3}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Trạng thái',
      dataIndex: 'Status',
      key: 'Status',
      width: 120,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status === 'active' ? 'Đang dùng' : 'Tạm ngưng'}
        </Tag>
      )
    },
    {
      title: 'Duyệt',
      dataIndex: 'ReviewStatus',
      key: 'ReviewStatus',
      width: 120,
      render: (reviewStatus: string) => {
        let color = 'default';
        let text = 'Chờ duyệt';
        if (reviewStatus === 'approved') {
          color = 'green';
          text = 'Đã duyệt';
        } else if (reviewStatus === 'rejected') {
          color = 'red';
          text = 'Từ chối';
        }
        return <Tag color={color}>{text}</Tag>;
      }
    },
    {
      title: 'Tỷ lệ thành công',
      dataIndex: 'SuccessRate',
      key: 'SuccessRate',
      width: 120,
      render: (rate: number | null) => 
        rate !== null ? `${(rate * 100).toFixed(1)}%` : 'N/A'
    },
    {
      title: 'Lượt dùng',
      dataIndex: 'UseCount',
      key: 'UseCount',
      width: 100
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right',
      width: 220,
      render: (_, record) => (
        <Space size="small">
          {record.ReviewStatus === 'pending' && (
            <>
              <Tooltip title="Duyệt">
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  size="small"
                  onClick={() => handleApprove(record.PairID)}
                  style={{ color: '#52c41a' }}
                />
              </Tooltip>
              <Tooltip title="Từ chối">
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  size="small"
                  onClick={() => handleReject(record.PairID)}
                  danger
                />
              </Tooltip>
            </>
          )}
          <Tooltip title="Generate Embedding">
            <Button
              type="text"
              icon={<ThunderboltOutlined />}
              size="small"
              onClick={() => handleGenerateEmbedding(record.PairID)}
              style={{ color: '#faad14' }}
            />
          </Tooltip>
          <Tooltip title="Sửa">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => navigate(`/admin/virtual-advisor/qa-pairs/${record.PairID}/edit`)}
            />
          </Tooltip>
          <Popconfirm
            title="Xóa Q&A pair này?"
            description="Hành động này không thể hoàn tác."
            onConfirm={() => handleDelete(record.PairID)}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Xóa">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                danger
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card
      title="Quản lý Q&A Pairs"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/admin/virtual-advisor/qa-pairs/new')}
        >
          Thêm mới
        </Button>
      }
    >
      {/* Filters */}
      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          placeholder="Tìm trong câu hỏi/câu trả lời..."
          allowClear
          enterButton={<SearchOutlined />}
          style={{ width: 300 }}
          onSearch={handleSearch}
        />
        <Select
          placeholder="Trạng thái"
          allowClear
          style={{ width: 150 }}
          onChange={(value) => handleFilterChange('status', value)}
        >
          <Option value="active">Đang dùng</Option>
          <Option value="inactive">Tạm ngưng</Option>
        </Select>
        <Select
          placeholder="Duyệt"
          allowClear
          style={{ width: 150 }}
          onChange={(value) => handleFilterChange('reviewStatus', value)}
        >
          <Option value="pending">Chờ duyệt</Option>
          <Option value="approved">Đã duyệt</Option>
          <Option value="rejected">Từ chối</Option>
        </Select>
        <Select
          placeholder="Danh mục"
          allowClear
          style={{ width: 200 }}
          onChange={(value) => handleFilterChange('category', value)}
        >
          <Option value="Quy chế">Quy chế</Option>
          <Option value="Học phí">Học phí</Option>
          <Option value="Lịch học">Lịch học</Option>
          <Option value="Điểm số">Điểm số</Option>
          <Option value="Khác">Khác</Option>
        </Select>
      </Space>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={qaPairs}
        rowKey="PairID"
        loading={loading}
        pagination={{
          current: filters.page,
          pageSize: filters.limit,
          total: total,
          showSizeChanger: true,
          showTotal: (total) => `Tổng ${total} Q&A pairs`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        onChange={handleTableChange}
        scroll={{ x: 1800 }}
        size="middle"
      />
    </Card>
  );
};

export default QAPairList;
